const componentSelectorFilter = (comp) => {
  return comp?.__typename === 'ComponentSelector';
};

const hasOwn = (object, property) => {
  return Object.prototype.hasOwnProperty.call(object || {}, property);
};

const getRootContainerComponents = (componentData = [], containerKeys = {}) => {
  return componentData.reduce(
    (acc, sections) => {
      if (hasOwn(containerKeys, sections?.__typename)) {
        return [...acc, ...(sections[containerKeys[sections.__typename]] || [])];
      }
      return [...acc, ...(sections.components || [])];
    }, []);
};

const getNestedContainerVariations = (components = [], containerKeys = {}) => {
  return components.filter((comp) => {
    return hasOwn(containerKeys, comp?.__typename);
  }).map((container) => {
    const resolver = containerKeys[container.__typename];
    if (typeof resolver === 'function') {
      const result = resolver(container) || [];
      return (Array.isArray(result) ? result : [result]).filter(componentSelectorFilter);
    }
    return container[resolver].filter(componentSelectorFilter);
  }).reduce((acc, componentSelectors) => {
    return [...acc, ...(componentSelectors || [])];
  }, []);
};

/**
 * @function getObjectValue
 * @param {Object} obj - The object to retrieve the value from
 * @param {String} path - Dot-notation string path to the desired property (e.g., 'user.address.city')
 * @description Safely retrieves a nested value from an object using a dot-notation path string.
 * Uses optional chaining to avoid errors when traversing properties that may not exist.
 * @returns {*} The value at the specified path, or undefined if the path doesn't exist
 * @example
 * const obj = { user: { profile: { name: 'John' } } };
 * getObjectValue(obj, 'user.profile.name'); // Returns 'John'
 * getObjectValue(obj, 'user.settings.theme'); // Returns undefined
 */
const getObjectValue = (obj, path) => {
  return path
    .split('.')
    .reduce((accumulator, nextKeyInPath) => accumulator?.[nextKeyInPath], obj);
};

const getVariationsFromContent = (content, containerKeys) => {
  const variations = [];

  Object.keys(content).forEach((key) => {
    const componentData = content[key];
    const isRootArrayContentNode = Array.isArray(componentData) && key !== 'breadCrumbs';
    const isContentfulComponent = !!componentData?.__typename || isRootArrayContentNode;
    // This will work for specialized containers directly at the root (which is none as of q3 2024)
    const isSpecializedContainer = !!componentData?.__typename && hasOwn(containerKeys, componentData?.__typename);
    const componentsKey = isSpecializedContainer ? containerKeys[componentData.__typename] : '';

    if (isContentfulComponent) {
      let childComponents = [];
      if (isRootArrayContentNode) {
        childComponents = getRootContainerComponents(componentData, containerKeys);
      } else if (isSpecializedContainer) {
        // If a Specialized Container is directly at the root
        // Support both string and array of paths for containerKeys values
        const paths = Array.isArray(componentsKey) ? componentsKey : [componentsKey];
        paths.forEach((path) => {
          const components = getObjectValue(componentData, path) || [];
          childComponents.push(...components);
        });
      } else {
        childComponents = componentData.components || [];
      }

      const nestedVariations = getNestedContainerVariations(childComponents, containerKeys);
      const rootVariations = childComponents.filter(componentSelectorFilter);
      variations.push(...nestedVariations);
      variations.push(...rootVariations);
    }
  });

  return variations;
};

/**
 * @function getContentfulTargetingIds
 * @param {{content: ContentfulContent}} config
 * @description Takes the content key from a typical Contentful response, and loops through the entire
 * object, pulling out any mboxes or innervateTags that are associated with a ComponentSelector.
 * @returns {{
 * mboxes: String[],
 * innervateTags: [{ tagID: String, adkey: String, csId: String, campaignMetadata: String }]
 * }}
 */
export const getContentfulTargetingIds = ({ content, containerKeys }) => {
  const mboxes = [];
  const innervateTags = [];

  if (!content) {
    return { mboxes, innervateTags };
  }

  const variations = getVariationsFromContent(content, containerKeys);
  variations.forEach((comp) => {
    const { targetingType, id, campaignMetadata } = comp;

    if (targetingType?.__typename === 'AdobeTarget') {
      mboxes.push(targetingType.mbox);
    } else if (targetingType?.__typename === 'Innervate') {
      innervateTags.push({
        tagID: targetingType.tagID,
        adkey: targetingType.adkey,
        csId: id,
        campaignMetadata,
      });
    }
  });

  return { mboxes, innervateTags };
};
